import { type AriaRole, type FocusEvent, forwardRef } from "react"

import type { SX } from "../../theme"
import { BaseInput } from "../BaseInput"
import { Box } from "../Box"
import { Icon } from "../Icon"
import { ProgressCircle } from "../ProgressCircle"
import styles from "./SearchInput.module.css"

export interface SearchInputProps {
  value: string
  onValueChange: (value: string) => void
  id?: string
  placeholder?: string
  maxLength?: number
  isLoading?: boolean
  sx?: SX
  role?: AriaRole
  onFocus?: (event: FocusEvent<HTMLInputElement, Element>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement, Element>) => void
  startAdornment?: boolean
  endAdornment?: boolean
  /**
   * AutoFocus can cause assesibilty issues when used, especially in forms.
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autofocus#accessibility_concerns
   */
  autoFocus?: boolean
  disabled?: boolean
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(props: SearchInputProps, ref) {
    const {
      id,
      value,
      placeholder,
      maxLength,
      isLoading = false,
      sx,
      onValueChange,
      role,
      onFocus,
      onBlur,
      startAdornment = true,
      endAdornment = true,
      autoFocus,
      disabled = false,
    } = props

    return (
      <BaseInput
        id={id}
        ref={ref}
        role={role}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        padding={{
          adornment: { inline: 12 },
          input: { left: startAdornment ? 0 : 12 },
        }}
        startAdornment={
          startAdornment && <StartAdornment isLoading={isLoading} />
        }
        endAdornment={
          endAdornment && (
            <EndAdornment value={value} onChange={onValueChange} />
          )
        }
        sx={sx}
        onValueChange={onValueChange}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        disabled={disabled}
      />
    )
  },
)

interface StartAdornmentProps {
  isLoading: boolean
}

function StartAdornment(props: StartAdornmentProps) {
  const { isLoading } = props

  if (isLoading)
    return (
      <Box width={20} height={20} alignItems="center" justifyContent="center">
        <ProgressCircle color="purple11" />
      </Box>
    )

  return <Icon name="search" color="grey11" size="medium" />
}

interface EndAdornmentProps {
  value: string
  onChange: (value: string) => void
}

function EndAdornment(props: EndAdornmentProps) {
  const { value, onChange } = props

  if (value.length === 0) return null

  return (
    <button className={styles.clearButton} onClick={() => onChange("")}>
      <Icon name="close" color="inherit" size="medium" />
    </button>
  )
}
